/**
 * DataTable
 * 
 * Wraps MUI Table with standardized API for all four states:
 *   loading (skeleton rows), error (error banner with retry),
 *   empty (centered message), populated.
 * 
 * Used by: N2 (customer list), N8 (scoreboard), N12 (inventory), N13 (entry log)
 * Never rebuild a table from scratch — always use this component.
 * 
 * Props:
 *   columns: [{key, label, sortable?, render?, align?, width?}]
 *   rows: array of row objects
 *   loading: boolean
 *   error: string | null
 *   emptyMessage: string (shown when rows.length === 0 and no error)
 *   noMatchMessage: string (shown when filtering yields 0 results)
 *   onSort: (key, direction) => void
 *   sortKey: string (current sort column key)
 *   sortDirection: 'asc' | 'desc'
 *   pagination: { page, rowsPerPage, totalCount, onPageChange, onRowsPerPageChange } | null
 *   onRetry: () => void (called when retry button clicked in error state)
 *   onRowClick: (row) => void
 *   stickyHeader: boolean (default: false)
 */

import React from 'react';
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableSortLabel,
  TablePagination,
  TableContainer,
  Paper,
  Skeleton,
  Typography,
  Button,
  Box,
  Alert,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import RefreshIcon from '@mui/icons-material/Refresh';

const SKELETON_ROWS = 5;

export default function DataTable({
  columns = [],
  rows = [],
  loading = false,
  error = null,
  emptyMessage = 'No data',
  noMatchMessage = 'No results match your filters',
  onSort,
  sortKey,
  sortDirection = 'asc',
  pagination,
  onRetry,
  onRowClick,
  stickyHeader = false,
  isFiltered = false,
  sx = {},
}) {
  const theme = useTheme();

  // ── Error state ──────────────────────────────────────────────
  if (error) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Alert
          severity="error"
          action={
            onRetry ? (
              <Button
                color="inherit"
                size="small"
                startIcon={<RefreshIcon />}
                onClick={onRetry}
              >
                Retry
              </Button>
            ) : null
          }
          sx={{ justifyContent: 'center' }}
        >
          {error}
        </Alert>
      </Box>
    );
  }

  // ── Empty / no-match state ───────────────────────────────────
  if (!loading && rows.length === 0) {
    return (
      <Box sx={{ p: 6, textAlign: 'center' }}>
        <Typography
          variant="body1"
          color="text.secondary"
          sx={{ fontStyle: 'italic' }}
        >
          {isFiltered ? noMatchMessage : emptyMessage}
        </Typography>
      </Box>
    );
  }

  return (
    <Paper
      elevation={0}
      sx={{
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 1,
        overflow: 'hidden',
        ...sx,
      }}
    >
      <TableContainer>
        <Table stickyHeader={stickyHeader} size="small">
          {/* ── Header ──────────────────────────────────────── */}
          <TableHead>
            <TableRow>
              {columns.map((col) => (
                <TableCell
                  key={col.key}
                  align={col.align || 'left'}
                  sx={{
                    ...theme.typography.subtitle2,
                    fontWeight: 600,
                    backgroundColor: theme.palette.background.paper,
                    borderBottom: `2px solid ${theme.palette.divider}`,
                    width: col.width,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {col.sortable && onSort ? (
                    <TableSortLabel
                      active={sortKey === col.key}
                      direction={sortKey === col.key ? sortDirection : 'asc'}
                      onClick={() => {
                        const newDir = sortKey === col.key && sortDirection === 'asc' ? 'desc' : 'asc';
                        onSort(col.key, newDir);
                      }}
                    >
                      {col.label}
                    </TableSortLabel>
                  ) : (
                    col.label
                  )}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          {/* ── Body ────────────────────────────────────────── */}
          <TableBody>
            {loading
              ? /* Skeleton rows */
                Array.from({ length: SKELETON_ROWS }).map((_, rowIdx) => (
                  <TableRow key={`skeleton-${rowIdx}`}>
                    {columns.map((col) => (
                      <TableCell key={col.key}>
                        <Skeleton variant="text" width="80%" />
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              : /* Data rows */
                rows.map((row, rowIdx) => (
                  <TableRow
                    key={row.id || rowIdx}
                    hover
                    onClick={onRowClick ? () => onRowClick(row) : undefined}
                    sx={{
                      cursor: onRowClick ? 'pointer' : 'default',
                      '&:last-child td': { borderBottom: 0 },
                    }}
                  >
                    {columns.map((col) => (
                      <TableCell
                        key={col.key}
                        align={col.align || 'left'}
                        sx={{
                          ...theme.typography.body2,
                          borderBottom: `1px solid ${theme.palette.divider}`,
                        }}
                      >
                        {col.render
                          ? col.render(row[col.key], row)
                          : row[col.key] ?? '—'}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* ── Pagination ──────────────────────────────────────── */}
      {pagination && (
        <TablePagination
          component="div"
          count={pagination.totalCount || rows.length}
          page={pagination.page || 0}
          rowsPerPage={pagination.rowsPerPage || 25}
          onPageChange={pagination.onPageChange || (() => {})}
          onRowsPerPageChange={pagination.onRowsPerPageChange || (() => {})}
          rowsPerPageOptions={[10, 25, 50, 100]}
          sx={{
            borderTop: `1px solid ${theme.palette.divider}`,
          }}
        />
      )}
    </Paper>
  );
}
